/* eslint-disable no-empty-pattern */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import {
    useLazyGetAllChannelsQuery,
    useLazyGetAllLoadsQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import React from 'react';

import { setWidgetDevice } from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';

import { getWidgetTypeValidationConfig, retrieveChannelTypefromWidgetType } from '@fiji/common/src/utils/helpers';
import { toFirstLetterUpperCase } from 'utils/helpers';

type ReturnProps = {
    loadingHierarchyNode: any[];
    handleFetchHierarchy: (...args: any) => Promise<void>;
    hasChildNode: (...args: any) => boolean;
    hierarchyMappingData: any;
    handleCleanHierarchyNode: (...args: any) => void;
    groupsData: any;
};

export const useChannelHierarchyHandlers = ({ widgetType }: any): ReturnProps => {
    const selectedOrg = useTypedSelector((state) => state.org.selectedOrg);

    const dispatch = useAppDispatch();
    const selectedDeviceNode = useTypedSelector((state) => state.widgetConfiguration.device);
    const selectedSource = useTypedSelector((state) => state.widgetConfiguration.source);

    const [getAllLoads] = useLazyGetAllLoadsQuery();
    const [getAllChannels] = useLazyGetAllChannelsQuery();
    const [getGroupChildren, { data }] = useGetAllGroupsMutation({ fixedCacheKey: 'cachedAllGroupData' });
    // const [getGroupChildrenForTariff] = useGetAllGroupsChildrenForTariffMutation();

    const [groupsData, setGroupsData] = React.useState([]);
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState<any>({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState([]);
    const isSourceRequired = getWidgetTypeValidationConfig(widgetType)['isSourceRequired'];
    const isGaugeChartOrTrends = widgetType === 'gauge' || widgetType === 'charts' || widgetType === 'trends';

    React.useEffect(() => {
        if (!groupsData?.length) {
            setGroupsData(data?.data?.records);
        }
    }, [data]);

    React.useEffect(() => {
        if (widgetType && !selectedDeviceNode && !isSourceRequired) {
            dispatch(setWidgetDevice({ data: selectedOrg, reset: false }));
        }
    }, [groupsData, widgetType]);

    React.useEffect(() => {
        if (selectedOrg?.id) {
            getGroupChildren({}) as any;
        }
    }, [selectedOrg?.id]);

    const buildChannelNodes = (channelsData: any[], currentNode: any): any[] => {
        if (!channelsData?.length) {
            return [
                {
                    id: `${currentNode?.id}-no-channels`,
                    name: `No ${toFirstLetterUpperCase(widgetType)} found`,
                    type: 'DEVICE',
                    noIcon: true,
                },
            ];
        }

        return channelsData.map((item: any) => ({
            id: `${item?.channelId}${currentNode?.id}`,
            name: item?.displayName,
            type: 'channel',
            ...item,
        }));
    };

    const appendHierarchyNodes = (nodeId: string, items: any[]): void => {
        setHierarchyMappingData((prev: any) => ({
            ...prev,
            [nodeId]: [...(prev[nodeId] ?? []), ...(items ?? [])],
        }));
    };

    const appendChildHierarchy = (nodeId: string, records: any[], initializeWhenEmpty = false): void => {
        if (!records?.length && !initializeWhenEmpty) return;
        appendHierarchyNodes(nodeId, records ?? []);
    };

    const addLoadingNode = (nodeId?: string): void => {
        if (!nodeId) return;
        setLoadingHierarchyNode((prevState): any => [...prevState, nodeId]);
    };

    const removeLoadingNode = (nodeId?: string): void => {
        if (!nodeId) return;
        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== nodeId));
    };

    const runWithLoadingNode = async (nodeId?: string, action?: () => Promise<void>): Promise<void> => {
        if (!nodeId || !action) return;
        addLoadingNode(nodeId);
        try {
            await action();
        } finally {
            removeLoadingNode(nodeId);
        }
    };

    const fetchGatewayChannel = async (currentNode: any): Promise<void> => {
        const { data: childHierarchyData }: any = await getGroupChildren({ parent: currentNode?.id });
        const childRecords = childHierarchyData?.data?.records ?? [];

        const { data: channels }: any = await getAllChannels({
            sourceId: currentNode?.deviceTypeId ?? '',
            params: {
                channelType: retrieveChannelTypefromWidgetType(widgetType),
            },
            number: isGaugeChartOrTrends,
        });

        appendChildHierarchy(currentNode?.id, childRecords);

        const shouldAppendChannels = channels?.success && childRecords.length && isSourceRequired && !selectedSource;

        if (!shouldAppendChannels) return;

        appendHierarchyNodes(currentNode?.id, buildChannelNodes(channels?.data, currentNode));
    };

    const fetchDeviceChannel = async (currentNode: any): Promise<void> => {
        const { data: channels }: any = await getAllChannels({
            sourceId: currentNode?.deviceTypeId ?? '',
            params: {
                channelType: retrieveChannelTypefromWidgetType(widgetType),
            },
            number: widgetType === 'gauge' || widgetType === 'charts',
        });
        if (channels?.success) {
            setHierarchyMappingData((prev: any) => ({
                ...prev,
                [currentNode?.id]: channels?.data?.length
                    ? channels?.data?.map((item: any) => ({
                          id: `${item?.channelId}${currentNode?.id}`,
                          name: item?.displayName,
                          type: 'channel',
                          ...item,
                      }))
                    : [
                          {
                              id: `${currentNode?.id}-no-channels`,
                              name: `No ${toFirstLetterUpperCase(widgetType)} found`,
                              type: 'DEVICE',
                              noIcon: true,
                          },
                      ],
            }));
        }
    };

    const fetchGroupHierarchy = async (currentNode: any): Promise<void> => {
        const { data: childHierarchyData }: any = await getGroupChildren({ parent: currentNode?.id });
        if (!childHierarchyData?.success) return;
        appendChildHierarchy(currentNode?.id, childHierarchyData?.data?.records ?? [], true);
    };

    const getHierarchyFetcher = (currentNode: any): ((node: any) => Promise<void>) => {
        if (currentNode?.type === 'GATEWAY') return fetchGatewayChannel;
        if (currentNode?.deviceTypeId && currentNode?.type === 'DEVICE') return fetchDeviceChannel;
        return fetchGroupHierarchy;
    };

    const handleFetchHierarchy = async (currentNode: any): Promise<void> => {
        const nodeId = currentNode?.id;
        if (!nodeId) return;
        const hierarchyFetcher = getHierarchyFetcher(currentNode);
        await runWithLoadingNode(nodeId, async () => {
            await hierarchyFetcher(currentNode);
        });
    };

    const buildDeviceLoadNodes = (parentNode: any, loadsData: any): any[] =>
        loadsData?.data?.loadConfiguration?.length
            ? loadsData?.data?.loadConfiguration?.map((item: any) => ({
                  loadId: item?.loadId,
                  id: `${item?.loadId}${parentNode?.id}`?.replace(/-/g, '_'),
                  name: item?.loadName,
                  type: 'load',
                  groupPath: [...parentNode.groupPath],
                  icon: {
                      web: {
                          family: 'loads',
                          loadNumber: parseInt(item?.loadNumber ?? 0) + 1,
                          sx: {
                              fontSize: '14px',
                              fontWeight: '600',
                              backgroundColor: '#727E84',
                              color: '#fff',
                              width: '18px',
                              height: '18px',
                              borderRadius: '4px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                          },
                      },
                  },
                  deviceId: parentNode?.id,
              }))
            : [
                  {
                      id: `${parentNode?.id}-no-loads`,
                      name: 'No Controllable Loads',
                      type: 'DEVICE',
                      noIcon: true,
                  },
              ];

    const fetchLoadsForDeviceNode = async (parentNode: any): Promise<void> => {
        const { data: loadsData }: any = await getAllLoads({
            body: { page: 0, size: 10 },
            deviceId: parentNode?.id,
        });

        setHierarchyMappingData((prev: any) => ({
            ...prev,
            [parentNode?.id]: buildDeviceLoadNodes(parentNode, loadsData),
        }));
    };

    const fetchLoadsForGroupNode = async (parentNode: any): Promise<void> => {
        const { data: childHierarchyData }: any = await getGroupChildren({ parent: parentNode?.id });
        if (!childHierarchyData?.success) return;
        setHierarchyMappingData((prev: any) => ({
            ...prev,
            [parentNode?.id]: [...(prev[parentNode?.id] ?? []), ...(childHierarchyData.data?.records ?? [])],
        }));
    };

    const handleFetchLoadsHierarchy = async (parentNode: any): Promise<void> => {
        const nodeId = parentNode?.id;
        if (!nodeId) return;
        const fetchLoadsHierarchy = parentNode?.deviceTypeId ? fetchLoadsForDeviceNode : fetchLoadsForGroupNode;
        await runWithLoadingNode(nodeId, async () => {
            await fetchLoadsHierarchy(parentNode);
        });
    };

    const handleCleanHierarchyNode = (parentId: string): void => {
        setHierarchyMappingData((prevState: any) => {
            const newHierarchyMappingData: any = { ...prevState };
            delete newHierarchyMappingData[parentId];
            return newHierarchyMappingData;
        });
    };

    const hasChildNode = (treeItem: any): boolean =>
        treeItem?.groupCount ||
        treeItem?.deviceCount ||
        hierarchyMappingData[treeItem?.id]?.length ||
        (treeItem?.deviceTypeId && isSourceRequired && !selectedSource);

    return {
        loadingHierarchyNode,
        handleFetchHierarchy: widgetType === 'loads' ? handleFetchLoadsHierarchy : handleFetchHierarchy,
        hasChildNode,
        hierarchyMappingData,
        handleCleanHierarchyNode,
        groupsData,
    };
};
